// ╔══════════════════════════════════════════════════════════════════════════╗
/* ║  COPIAR ARQUIVO → src/pages/CostAllocationPage.js                       ║
 * ║  Versão: 1.8.1 – Remoção de variável não utilizada (lint fix)           ║
 * ║  Data: 21/11/2025                                                       ║
 * ║  Responsável pela Alteração: ChatGPT (conforme Protocolo)              ║
 * ║                                                                          ║
 * ║  Histório relevante:                                                    ║
 * ║   • 1.8   – Chamada da Cloud Function allocateAcquisitionCosts.        ║
 * ║   • 1.8.1 – Removida a constante expensesToAllocate não utilizada,     ║
 * ║             eliminando o warning do ESLint (no-unused-vars).           ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
/**
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema Golden Sisters
 * Arquivo: src/pages/CostAllocationPage.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import { getPurchases, getFinancialExpensesByCategories, getFinancialCategories } from '../services/firestoreService';
import { Loader2 } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/config';

const CostAllocationPage = ({ openModal }) => {
  const [purchases, setPurchases] = useState([]);
  const [acquisitionExpenses, setAcquisitionExpenses] = useState([]);
  const [allFinancialCategories, setAllFinancialCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAllocating, setIsAllocating] = useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedExpenseIds, setSelectedExpenseIds] = useState([]);

  const functions = getFunctions(app, 'southamerica-east1');
  const allocateCostsCallable = httpsCallable(functions, 'allocateAcquisitionCosts');

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const fetchedPurchases = await getPurchases();
      fetchedPurchases.sort(
        (a, b) => (b.purchaseDate?.toDate() || 0) - (a.purchaseDate?.toDate() || 0)
      );
      setPurchases(fetchedPurchases);

      const fetchedAllFinancialCategories = await getFinancialCategories();
      setAllFinancialCategories(fetchedAllFinancialCategories);

      const acquisitionCategoryNames = [
        'Frete de Compras',
        'Impostos sobre Compras',
        'Pedágios (Viagens de Compra)',
        'Combustível (Viagens de Compra)',
        'Alimentação (Viagens de Compra)',
        'Outros Custos de Aquisição',
      ];

      const relevantCategoryIds = fetchedAllFinancialCategories
        .filter(
          (cat) =>
            acquisitionCategoryNames.includes(cat.name) && cat.type === 'expense'
        )
        .map((cat) => cat.id);

      const now = new Date();
      const defaultStartDate = startDate
        ? new Date(startDate)
        : new Date(now.getFullYear(), now.getMonth(), 1);
      const defaultEndDate = endDate
        ? new Date(endDate)
        : new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      let fetchedExpenses = [];
      if (relevantCategoryIds.length > 0) {
        fetchedExpenses = await getFinancialExpensesByCategories(
          relevantCategoryIds,
          defaultStartDate,
          defaultEndDate,
          selectedPurchaseId
        );
        setAcquisitionExpenses(
          fetchedExpenses.filter((exp) => !exp.isAllocated)
        );
      } else {
        setAcquisitionExpenses([]);
        console.warn(
          'Nenhuma categoria de custo de aquisição relevante encontrada. ' +
            'Por favor, certifique-se de que as categorias padrão foram criadas no Módulo Financeiro.'
        );
      }
    } catch (error) {
      console.error(
        'Erro ao carregar dados para a página de Rateio de Custos:',
        error
      );
      openModal(
        <p className="text-red-500">
          Não foi possível carregar os dados para rateio de custos. Verifique o
          console (F12) para detalhes.
        </p>,
        'Erro de Carregamento'
      );
    } finally {
      setIsLoading(false);
    }
  }, [openModal, selectedPurchaseId, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleExpenseSelection = (expenseId) => {
    setSelectedExpenseIds((prevSelected) =>
      prevSelected.includes(expenseId)
        ? prevSelected.filter((id) => id !== expenseId)
        : [...prevSelected, expenseId]
    );
  };

  const handleApplyAllocation = async () => {
    if (selectedExpenseIds.length === 0) {
      openModal(
        <p className="text-red-500">
          Selecione pelo menos uma despesa de aquisição para ratear.
        </p>,
        'Nenhuma Despesa Selecionada'
      );
      return;
    }
    if (!selectedPurchaseId) {
      openModal(
        <p className="text-red-500">
          Selecione uma Compra Específica para aplicar o rateio.
        </p>,
        'Nenhuma Compra Selecionada'
      );
      return;
    }

    const purchaseToAllocate = purchases.find(
      (p) => p.id === selectedPurchaseId
    );

    if (!purchaseToAllocate) {
      openModal(
        <p className="text-red-500">Compra selecionada não encontrada.</p>,
        'Erro'
      );
      return;
    }
    if (!purchaseToAllocate.items || purchaseToAllocate.items.length === 0) {
      openModal(
        <p className="text-red-500">
          A compra selecionada não possui produtos para rateio.
        </p>,
        'Erro'
      );
      return;
    }

    setIsAllocating(true);
    try {
      const result = await allocateCostsCallable({
        purchaseId: selectedPurchaseId,
        expenseIds: selectedExpenseIds,
      });

      if (result.data.success) {
        openModal(
          <p className="text-green-500">{result.data.message}</p>,
          'Rateio Concluído!'
        );
        await loadData();
        setSelectedExpenseIds([]);
      } else {
        openModal(
          <p className="text-red-500">
            {result.data.message || 'Erro desconhecido ao ratear custos.'}
          </p>,
          'Erro no Rateio'
        );
      }
    } catch (error) {
      console.error('Erro ao chamar Cloud Function de rateio:', error);
      openModal(
        <p className="text-red-500">
          Ocorreu um erro ao processar o rateio: {error.message}
        </p>,
        'Erro de Comunicação'
      );
    } finally {
      setIsAllocating(false);
    }
  };

  const formatCurrency = (value) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (date) =>
    date
      ? date.toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : 'N/A';

  const getCategoryName = useCallback(
    (categoryRef) => {
      const category = allFinancialCategories.find(
        (cat) => cat.id === categoryRef?.id
      );
      return category ? category.name : 'Desconhecida';
    },
    [allFinancialCategories]
  );

  const totalAcquisitionExpenses = acquisitionExpenses.reduce(
    (sum, exp) => sum + exp.amount,
    0
  );
  const selectedAcquisitionExpensesTotal = acquisitionExpenses
    .filter((exp) => selectedExpenseIds.includes(exp.id))
    .reduce((sum, exp) => sum + exp.amount, 0);

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-brand-black text-white">
        <Loader2 className="h-12 w-12 animate-spin text-brand-gold" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-brand-black min-h-screen text-white font-sans">
      <h1 className="text-3xl font-bold font-serif mb-6">
        Rateio de Custos de Aquisição
      </h1>
      <p className="mb-8 text-gray-300">
        Aqui você pode visualizar os gastos adicionais de compras e
        distribuí-los proporcionalmente entre os produtos para calcular o custo
        real.
      </p>

      <div className="bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-200">
          Filtrar Compras e Gastos
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label
              htmlFor="purchaseSelect"
              className="block text-sm font-medium text-gray-400"
            >
              Selecionar Compra Específica
            </label>
            <select
              id="purchaseSelect"
              value={selectedPurchaseId}
              onChange={(e) => setSelectedPurchaseId(e.target.value)}
              className="w-full mt-1 bg-gray-800 p-2 rounded-md border border-gray-600 text-white focus:ring-brand-gold focus:border-brand-gold"
            >
              <option value="">Todas as Compras</option>
              {purchases.map((p) => (
                <option key={p.id} value={p.id}>
                  {formatDate(p.purchaseDate?.toDate())} - NF: {p.invoiceNumber}{' '}
                  - R${' '}
                  {p.totalPurchaseValue
                    ?.toFixed(2)
                    .replace('.', ',')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="startDate"
              className="block text-sm font-medium text-gray-400"
            >
              Data de Início (Gastos)
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full mt-1 bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
            />
          </div>
          <div>
            <label
              htmlFor="endDate"
              className="block text-sm font-medium text-gray-400"
            >
              Data de Fim (Gastos)
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full mt-1 bg-gray-800 border-gray-600 rounded-md shadow-sm p-2 text-white focus:ring-brand-gold focus:border-brand-gold"
            />
          </div>
        </div>
        <button
          onClick={loadData}
          className="mt-4 px-6 py-2 bg-brand-gold text-brand-black font-semibold rounded-md hover:bg-yellow-500 transition-colors"
        >
          Aplicar Filtros
        </button>

        <div className="mt-6 text-xl font-bold text-gray-200">
          Total de Gastos de Aquisição (Filtrado):{' '}
          <span className="text-brand-gold">
            {formatCurrency(totalAcquisitionExpenses)}
          </span>
        </div>
      </div>

      <div className="bg-brand-dark p-6 rounded-lg mb-6 border border-gray-700">
        <h2 className="text-xl font-semibold mb-4 text-gray-200">
          Despesas de Aquisição Encontradas (
          {selectedAcquisitionExpensesTotal > 0
            ? `${formatCurrency(
                selectedAcquisitionExpensesTotal
              )} selecionados`
            : 'Nenhuma selecionada'}
          )
        </h2>
        {acquisitionExpenses.length === 0 ? (
          <p className="text-gray-500">
            Nenhuma despesa de aquisição encontrada para os filtros
            selecionados. Certifique-se de registrar despesas com as categorias
            corretas no Módulo Financeiro e vinculá-las à compra, se
            necessário.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th
                    scope="col"
                    className="p-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                  ></th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                  >
                    Data
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                  >
                    Categoria
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                  >
                    Descrição
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                  >
                    NF Compra
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider"
                  >
                    Valor
                  </th>
                </tr>
              </thead>
              <tbody className="bg-brand-dark divide-y divide-gray-700">
                {acquisitionExpenses.map((exp) => (
                  <tr key={exp.id}>
                    <td className="p-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedExpenseIds.includes(exp.id)}
                        onChange={() =>
                          handleToggleExpenseSelection(exp.id)
                        }
                        className="form-checkbox h-4 w-4 text-brand-gold rounded border-gray-600 focus:ring-brand-gold bg-gray-700"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(exp.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {getCategoryName(exp.categoryRef)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {exp.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {exp.purchaseInvoiceNumber || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-500">
                      {formatCurrency(exp.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-gray-800 p-4 rounded-lg flex justify-end items-center">
        <button
          onClick={handleApplyAllocation}
          disabled={
            selectedExpenseIds.length === 0 ||
            !selectedPurchaseId ||
            isAllocating
          }
          className="bg-brand-gold text-brand-black font-bold py-3 px-6 rounded-lg hover:bg-yellow-500 disabled:bg-gray-500 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center"
        >
          {isAllocating ? (
            <Loader2 className="animate-spin mr-2" />
          ) : (
            'Aplicar Rateio aos Produtos Selecionados'
          )}
        </button>
      </div>
    </div>
  );
};

export default CostAllocationPage;
